/**
 * KnowledgeBaseManager — the "Documents" + "Unanswered Questions" halves
 * of the AI Knowledge Base admin tab.
 *
 * The plain-text `AIKnowledge` entries (title + body) are still managed
 * inline in AdminPortal.js — this component adds:
 *
 *   1. Ingestable documents (PDF / DOCX / XLSX / TXT) that are parsed,
 *      chunked and embedded server-side into `ai_knowledge_chunks`.
 *   2. Unanswered-question flags raised whenever the RAG endpoint can't
 *      confidently answer a member's question, so admins know exactly
 *      what content to add next.
 *
 * All routes live in /app/backend/kb_routes.py.
 */
import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Trash2, Upload, FileText, AlertTriangle, CheckCircle2, RefreshCw,
  BarChart3, X, Tag, Filter, Copy, Plus, ShieldCheck, Clock, Sparkles,
} from 'lucide-react';
import QAPipelineDashboard from './QAPipelineDashboard';
import MasterfileEditor from './MasterfileEditor';

const humanSize = (bytes) => {
  if (!bytes && bytes !== 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const humanDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function KnowledgeBaseManager() {
  const { API } = useContext(AuthContext);
  const [docs, setDocs] = useState([]);
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [docTitle, setDocTitle] = useState('');
  const [uploadCategory, setUploadCategory] = useState('');
  const [uploadTags, setUploadTags] = useState('');
  const [resolutionDrafts, setResolutionDrafts] = useState({});
  const fileRef = useRef(null);
  // Coverage-report state — panel opens inline below the Documents card
  // rather than as a modal so admins can keep scrolling through it while
  // uploading more content.
  const [coverage, setCoverage] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [coverageOpen, setCoverageOpen] = useState(false);
  // Shared taxonomy — auto-seeds on first fetch. Editable inline.
  const [categories, setCategories] = useState([]);
  const [newCategoryDraft, setNewCategoryDraft] = useState('');
  const [savingCategories, setSavingCategories] = useState(false);
  // Filter/search on the document list.
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  // Bulk import modal state — matches the Text Entries Bulk Import UX.
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFiles, setBulkFiles] = useState([]);   // File[]
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);  // {done, total, created, dup, failed, failedDetails[]}
  const bulkFileRef = useRef(null);
  // Multi-select for bulk actions across the doc list (approve / delete / recategorize).
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null);  // 'approve' | 'delete' | 'recategorize' | null
  const [reassignCategory, setReassignCategory] = useState('');
  const [reassignTags, setReassignTags] = useState('');
  const [reassignBusy, setReassignBusy] = useState(false);
  // Smart-tag suggestions — populated by the AI after the admin clicks
  // "Suggest categories". Reviewed inline then applied via the existing
  // bulk-recategorize endpoint (one API call per unique category).
  const [suggestions, setSuggestions] = useState(null);  // [{doc_id, title, suggested_category, confidence}]
  const [suggestBusy, setSuggestBusy] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [dRes, fRes, cRes] = await Promise.all([
        axios.get(`${API}/admin/ai-knowledge/documents`),
        axios.get(`${API}/admin/ai-knowledge/flags?resolved=false`),
        axios.get(`${API}/admin/ai-knowledge/categories`),
      ]);
      setDocs(dRes.data || []);
      setFlags(fRes.data || []);
      setCategories(cRes.data?.categories || []);
    } catch (e) {
      toast.error('Failed to load knowledge base');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  // Poll while any document is still embedding in the background so the
  // status pill flips from "Embedding…" → "Pending review" or "Failed"
  // without the admin having to hit Refresh manually.
  useEffect(() => {
    const stillProcessing = docs.some((d) => d.status === 'processing');
    if (!stillProcessing) return undefined;
    const id = setInterval(() => { loadAll(); }, 3500);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [docs]);

  const onUpload = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File exceeds 20 MB limit');
      return;
    }
    const form = new FormData();
    form.append('file', file);
    if (docTitle.trim()) form.append('title', docTitle.trim());
    if (uploadCategory.trim()) form.append('category', uploadCategory.trim());
    if (uploadTags.trim()) form.append('tags', uploadTags.trim());
    setUploading(true);
    try {
      const res = await axios.post(`${API}/admin/ai-knowledge/documents`, form);
      const doc = res.data;
      if (doc?.status === 'processing') {
        toast.success(
          `Uploaded — embedding ${doc.chunk_count.toLocaleString()} chunks in the background. Approve it once status flips to Pending Review.`,
          { duration: 8000 },
        );
      } else if (doc?.duplicate_of) {
        toast.warning(
          'Uploaded — but this looks like a near-duplicate of an existing document. Review the highlighted match before approving.',
          { duration: 10000 },
        );
      } else if (doc?.status_message) {
        toast.warning(doc.status_message, { duration: 10000 });
      } else {
        toast.success('Uploaded — review the preview and click Approve to make it retrievable.');
      }
      setDocTitle('');
      setUploadTags('');
      // Keep category selected — admins usually upload a batch to one bucket.
      await loadAll();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Upload failed';
      toast.error(String(msg), { duration: 12000, style: { maxWidth: 520 } });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const approveDoc = async (id, title) => {
    try {
      await axios.post(`${API}/admin/ai-knowledge/documents/${id}/approve`);
      toast.success(`Approved "${title}" — chunks are now retrievable.`);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    }
  };

  const updateDocMeta = async (id, patch) => {
    try {
      await axios.patch(`${API}/admin/ai-knowledge/documents/${id}`, patch);
      toast.success('Document updated');
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  const saveCategories = async (next) => {
    setSavingCategories(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/ai-knowledge/categories`, { categories: next },
      );
      setCategories(data.categories || []);
      toast.success('Categories saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingCategories(false);
    }
  };

  const addCategory = () => {
    const n = newCategoryDraft.trim();
    if (!n) return;
    if (categories.includes(n)) {
      toast.error('Category already exists');
      return;
    }
    saveCategories([...categories, n]);
    setNewCategoryDraft('');
  };

  const removeCategory = (c) => {
    if (!window.confirm(`Remove category "${c}"? Existing documents keep their tag until manually re-categorized.`)) return;
    saveCategories(categories.filter((x) => x !== c));
  };

  // Download an original source file. Regular <a href> can't attach the
  // JWT Authorization header axios uses, so we fetch as a blob and
  // trigger the browser save via a temporary object URL.
  const downloadDoc = async (doc) => {
    if (!doc.storage_path) {
      toast.error(
        'Original file not archived for this document (uploaded before source archiving was enabled). Only the parsed text is in the KB.',
        { duration: 8000 },
      );
      return;
    }
    try {
      const res = await axios.get(
        `${API}/admin/ai-knowledge/documents/${doc.id}/download`,
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], {
        type: res.headers['content-type'] || doc.content_type || 'application/octet-stream',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename || `${doc.title || 'document'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Give the browser a tick before revoking so Safari finishes the save.
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Blob 4xx responses come back as blobs too — try to read the message.
      if (e?.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          toast.error(parsed?.detail || 'Download failed');
          return;
        } catch { /* fall through */ }
      }
      toast.error(typeof detail === 'string' ? detail : 'Download failed');
    }
  };

  // ---- Bulk import ---------------------------------------------------------
  const openBulkModal = () => {
    setBulkFiles([]);
    setBulkProgress(null);
    setBulkOpen(true);
  };

  const onBulkFilesPicked = (files) => {
    const list = Array.from(files || []).filter((f) => f.size > 0);
    // Client-side per-file cap matches the backend 20MB limit.
    const oversized = list.filter((f) => f.size > 20 * 1024 * 1024);
    if (oversized.length) {
      toast.error(`${oversized.length} file(s) exceed 20 MB and will be skipped.`);
    }
    setBulkFiles(list.filter((f) => f.size <= 20 * 1024 * 1024));
  };

  const runBulkImport = async () => {
    if (bulkFiles.length === 0) {
      toast.error('Pick at least one file first.');
      return;
    }
    setBulkRunning(true);
    setBulkProgress({
      done: 0, total: bulkFiles.length,
      created: 0, dup: 0, failed: 0, failedDetails: [],
    });
    let created = 0, dup = 0, failed = 0;
    const failedDetails = [];
    // Serial to keep the pending review queue orderly and to give the
    // ingress proxy a break between embed calls.
    for (let i = 0; i < bulkFiles.length; i++) {
      const f = bulkFiles[i];
      try {
        const form = new FormData();
        form.append('file', f);
        if (uploadCategory.trim()) form.append('category', uploadCategory.trim());
        if (uploadTags.trim()) form.append('tags', uploadTags.trim());
        const { data } = await axios.post(
          `${API}/admin/ai-knowledge/documents`, form,
          { timeout: 120000 },
        );
        if (data?.duplicate_of) dup += 1; else created += 1;
      } catch (e) {
        failed += 1;
        const detail = e?.response?.data?.detail;
        const msg =
          typeof detail === 'string' ? detail
          : Array.isArray(detail) ? detail.map((x) => x?.msg || JSON.stringify(x)).join('; ')
          : (e?.message || 'Upload failed');
        failedDetails.push({ filename: f.name, msg: String(msg).slice(0, 240) });
      }
      setBulkProgress({
        done: i + 1, total: bulkFiles.length,
        created, dup, failed, failedDetails: [...failedDetails],
      });
    }
    setBulkRunning(false);
    toast.success(
      `Bulk import complete — ${created} ok, ${dup} near-duplicate, ${failed} failed. Review the pending queue to approve.`,
      { duration: 12000 },
    );
    await loadAll();
  };

  // ---- Multi-select bulk actions on existing docs ------------------------
  const visibleDocs = docs.filter((d) => (
    (!filterCategory ||
      (filterCategory === '__uncat' ? !d.category : d.category === filterCategory)) &&
    (!filterStatus || d.status === filterStatus)
  ));

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    const visibleIds = visibleDocs.map((d) => d.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
    setSelectedIds(allSelected ? new Set() : new Set(visibleIds));
  };
  const clearSelection = () => setSelectedIds(new Set());

  const runBulkAction = async (action, extraBody = {}) => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (action === 'delete' && !window.confirm(`Delete ${ids.length} document${ids.length === 1 ? '' : 's'} and their chunks? This cannot be undone.`)) {
      return;
    }
    setReassignBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/ai-knowledge/documents/bulk`,
        { doc_ids: ids, action, ...extraBody },
      );
      if (action === 'approve') {
        toast.success(`Approved ${data.docs_updated} doc${data.docs_updated === 1 ? '' : 's'} · ${data.chunks_activated} chunks activated${data.skipped ? ` · ${data.skipped} skipped (not pending)` : ''}.`);
      } else if (action === 'delete') {
        toast.success(`Deleted ${data.docs_deleted} doc${data.docs_deleted === 1 ? '' : 's'} · ${data.chunks_removed} chunks removed.`);
      } else if (action === 'recategorize') {
        toast.success(`Updated ${data.docs_updated} doc${data.docs_updated === 1 ? '' : 's'}.`);
      }
      clearSelection();
      setBulkAction(null);
      setReassignCategory('');
      setReassignTags('');
      await loadAll();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Bulk action failed');
    } finally {
      setReassignBusy(false);
    }
  };

  // Ask the AI to propose one category per selected doc, based on the
  // title + preview text stored on each row. Doesn't write to the DB —
  // admin reviews the suggestions inline then hits "Apply suggestions"
  // which routes through the same bulk-recategorize endpoint (grouped
  // by target category so we make one API call per unique bucket).
  const suggestCategories = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (ids.length > 40) {
      toast.error('Suggestions are capped at 40 docs per run — narrow your selection.');
      return;
    }
    setSuggestBusy(true);
    setSuggestions(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/ai-knowledge/documents/suggest-categories`,
        { doc_ids: ids },
      );
      setSuggestions(data.suggestions || []);
      setBulkAction('suggest');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'AI suggestion failed');
    } finally {
      setSuggestBusy(false);
    }
  };

  const applySuggestions = async () => {
    if (!suggestions || !suggestions.length) return;
    // Group by target category so one bulk call per bucket.
    const buckets = {};
    for (const s of suggestions) {
      const cat = s.override_category || s.suggested_category || 'Uncategorized';
      if (!buckets[cat]) buckets[cat] = [];
      buckets[cat].push(s.doc_id);
    }
    setReassignBusy(true);
    let total = 0;
    try {
      for (const [cat, docIds] of Object.entries(buckets)) {
        const { data } = await axios.post(
          `${API}/admin/ai-knowledge/documents/bulk`,
          {
            doc_ids: docIds,
            action:  'recategorize',
            category: cat === 'Uncategorized' ? '' : cat,
          },
        );
        total += data.docs_updated || 0;
      }
      toast.success(`Applied categories to ${total} doc${total === 1 ? '' : 's'}.`);
      setSuggestions(null);
      setBulkAction(null);
      clearSelection();
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Apply failed');
    } finally {
      setReassignBusy(false);
    }
  };

  const deleteDoc = async (id, title) => {
    if (!window.confirm(`Delete "${title}" and all its chunks? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/ai-knowledge/documents/${id}`);
      toast.success('Document removed');
      await loadAll();
    } catch {
      toast.error('Delete failed');
    }
  };

  const resolveFlag = async (id) => {
    try {
      await axios.patch(`${API}/admin/ai-knowledge/flags/${id}`, {
        resolved: true,
        resolution_note: resolutionDrafts[id] || '',
      });
      toast.success('Flag marked resolved');
      await loadAll();
    } catch {
      toast.error('Update failed');
    }
  };

  const dismissFlag = async (id) => {
    if (!window.confirm('Dismiss this flag? It will be removed from the queue.')) return;
    try {
      await axios.delete(`${API}/admin/ai-knowledge/flags/${id}`);
      toast.success('Flag dismissed');
      await loadAll();
    } catch {
      toast.error('Dismiss failed');
    }
  };

  const reindex = async () => {
    try {
      const res = await axios.post(`${API}/admin/ai-knowledge/reindex`);
      toast.success(
        `Re-embedded ${res.data?.reindexed_text_entries ?? 0} text entries into ${res.data?.text_entry_chunks ?? 0} chunks.`
      );
      await loadAll();
    } catch {
      toast.error('Reindex failed');
    }
  };

  const [reindexingLms, setReindexingLms] = useState(false);
  const [reindexingForum, setReindexingForum] = useState(false);
  const [reindexingDiscussions, setReindexingDiscussions] = useState(false);
  // "Attach source" mini-workflow — legacy docs uploaded before source
  // archiving. We track which doc id is expecting a file so the hidden
  // input handler knows where to route the bytes.
  const [attachingDocId, setAttachingDocId] = useState(null);
  const attachRef = useRef(null);

  const reindexLms = async () => {
    setReindexingLms(true);
    try {
      const { data } = await axios.post(`${API}/admin/ai-knowledge/reindex-lms`);
      toast.success(
        `Indexed ${data.indexed_lessons} lessons (${data.chunks_written} chunks). ${data.skipped} skipped.`,
        { duration: 8000 },
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'LMS reindex failed');
    } finally {
      setReindexingLms(false);
    }
  };

  const reindexForum = async () => {
    setReindexingForum(true);
    try {
      const { data } = await axios.post(`${API}/admin/ai-knowledge/reindex-forum?min_replies=1`);
      toast.success(
        `Indexed ${data.indexed_threads} forum threads (${data.chunks_written} chunks). ${data.skipped} skipped.`,
        { duration: 8000 },
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Forum reindex failed');
    } finally {
      setReindexingForum(false);
    }
  };

  const reindexDiscussions = async () => {
    setReindexingDiscussions(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/ai-knowledge/reindex-lesson-discussions?min_replies=1`,
      );
      toast.success(
        `Seeded ${data.indexed_threads} lesson discussion${data.indexed_threads === 1 ? '' : 's'} into the KB (${data.chunks_written} chunks).`,
        { duration: 8000 },
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Discussion reindex failed');
    } finally {
      setReindexingDiscussions(false);
    }
  };

  const attachSource = async (docId, file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    try {
      await axios.post(
        `${API}/admin/ai-knowledge/documents/${docId}/attach-source`,
        form, { timeout: 120000 },
      );
      toast.success('Source file attached — Download button is now live.');
      setAttachingDocId(null);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Attach failed');
    }
  };

  const runCoverageReport = async () => {
    setCoverageOpen(true);
    setCoverageLoading(true);
    try {
      const res = await axios.get(`${API}/admin/ai-knowledge/coverage-report`);
      setCoverage(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Coverage report failed');
    } finally {
      setCoverageLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="kb-manager">
      {/* Enterprise QA Pipeline — long-term observability across every ask */}
      <QAPipelineDashboard />

      {/* Masterfile — the always-on operator's manual for the AI */}
      <MasterfileEditor />

      {/* Documents */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start gap-4 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" /> Knowledge Documents
              </CardTitle>
              <CardDescription>
                Upload PDF, DOCX, XLSX or TXT files. Each is parsed, chunked and embedded so
                the AI can answer with citations from your source material.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={openBulkModal}
              data-testid="kb-doc-bulk-import-btn"
              title="Upload many documents at once with shared category + tags"
            >
              <Upload className="w-4 h-4 mr-2" /> Bulk import
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={runCoverageReport}
              disabled={coverageLoading}
              data-testid="kb-coverage-report-btn"
              title="See which unanswered questions still have zero matching content"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              {coverageLoading ? 'Analyzing…' : 'Coverage report'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={reindex}
              disabled={loading}
              data-testid="kb-reindex-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reindex text entries
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={reindexLms}
              disabled={reindexingLms}
              data-testid="kb-reindex-lms-btn"
              title="Embed every published lesson body into the AI Knowledge Base so lessons are citable in AI answers."
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${reindexingLms ? 'animate-spin' : ''}`} />
              {reindexingLms ? 'Indexing lessons…' : 'Index LMS lessons'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={reindexForum}
              disabled={reindexingForum}
              data-testid="kb-reindex-forum-btn"
              title="Embed every forum thread with ≥1 reply into the AI Knowledge Base so community answers are citable."
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${reindexingForum ? 'animate-spin' : ''}`} />
              {reindexingForum ? 'Indexing forum…' : 'Index forum threads'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={reindexDiscussions}
              disabled={reindexingDiscussions}
              data-testid="kb-reindex-discussions-btn"
              title="Fold every lesson discussion thread (≥1 reply) into the KB as searchable evidence."
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${reindexingDiscussions ? 'animate-spin' : ''}`} />
              {reindexingDiscussions ? 'Seeding…' : 'Seed lesson discussions'}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Category taxonomy — inline editor. Shared with Manual Text Entries. */}
          <div className="rounded-lg border border-blue-200 dark:border-blue-900/40 bg-blue-50/40 dark:bg-blue-900/10 p-4">
            <div className="flex items-center gap-2 mb-2">
              <Tag className="w-4 h-4 text-blue-600 dark:text-blue-300" />
              <span className="text-sm font-semibold">Category taxonomy</span>
              <span className="text-xs text-slate-500">
                — shared with Manual Text Entries. Retrieval scoping keys off these.
              </span>
            </div>
            <div className="flex flex-wrap gap-2 mb-2" data-testid="kb-categories-list">
              {categories.map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
                >
                  {c}
                  <button
                    type="button"
                    onClick={() => removeCategory(c)}
                    className="ml-1 text-slate-400 hover:text-rose-600"
                    aria-label={`Remove ${c}`}
                    data-testid={`kb-category-remove-${c}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {categories.length === 0 && (
                <span className="text-xs text-slate-500 italic">
                  No categories yet — add one below.
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                value={newCategoryDraft}
                onChange={(e) => setNewCategoryDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCategory(); } }}
                placeholder="Add a new category (e.g. Freezers 101)"
                className="max-w-sm"
                data-testid="kb-category-new-input"
                disabled={savingCategories}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={addCategory}
                disabled={savingCategories || !newCategoryDraft.trim()}
                data-testid="kb-category-add-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          </div>

          {/* Upload form + Bulk import */}
          <div className="grid gap-3 md:grid-cols-4 items-end">
            <div className="md:col-span-2">
              <Label htmlFor="kb-doc-title">Optional title override</Label>
              <Input
                id="kb-doc-title"
                value={docTitle}
                onChange={(e) => setDocTitle(e.target.value)}
                placeholder="Leave blank to use filename"
                data-testid="kb-doc-title-input"
              />
            </div>
            <div>
              <Label htmlFor="kb-doc-category">Category</Label>
              <select
                id="kb-doc-category"
                value={uploadCategory}
                onChange={(e) => setUploadCategory(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="kb-doc-category-select"
              >
                <option value="">— none —</option>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="kb-doc-tags">Tags (comma-separated)</Label>
              <Input
                id="kb-doc-tags"
                value={uploadTags}
                onChange={(e) => setUploadTags(e.target.value)}
                placeholder="ppe, ventilation, quickref"
                data-testid="kb-doc-tags-input"
              />
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <input
              ref={fileRef}
              type="file"
              multiple
              accept=".pdf,.docx,.doc,.xlsx,.xls,.txt,.md,.csv,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              data-testid="kb-doc-file-input"
              onChange={async (e) => {
                const files = Array.from(e.target.files || []);
                if (!files.length) return;
                if (files.length === 1) {
                  await onUpload(files[0]);
                } else {
                  // Bulk import — process serially so the admin sees each
                  // near-duplicate warning and the server isn't hammered.
                  let ok = 0, dup = 0, fail = 0;
                  toast.info(`Uploading ${files.length} files…`, { duration: 4000 });
                  for (const f of files) {
                    try {
                      const form = new FormData();
                      form.append('file', f);
                      if (uploadCategory.trim()) form.append('category', uploadCategory.trim());
                      if (uploadTags.trim()) form.append('tags', uploadTags.trim());
                      const { data } = await axios.post(`${API}/admin/ai-knowledge/documents`, form);
                      if (data?.duplicate_of) dup += 1; else ok += 1;
                    } catch { fail += 1; }
                  }
                  toast.success(
                    `Bulk import complete — ${ok} ok, ${dup} flagged as duplicate, ${fail} failed. Review pending queue below to approve.`,
                    { duration: 12000 },
                  );
                  await loadAll();
                  if (fileRef.current) fileRef.current.value = '';
                }
              }}
            />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="kb-doc-upload-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Ingesting…' : 'Upload / Bulk import'}
            </Button>
            <p className="text-xs text-slate-500 self-center">
              Tip: pick multiple files at once for bulk import. Each lands in
              the pending queue for individual review.
            </p>
          </div>

          {/* Filter bar + select-all */}
          {docs.length > 0 && (
            <div className="flex items-center gap-2 pt-2 border-t border-slate-200 dark:border-slate-800 flex-wrap">
              <label className="flex items-center gap-1.5 text-xs cursor-pointer" title="Select every visible doc">
                <input
                  type="checkbox"
                  checked={visibleDocs.length > 0 && visibleDocs.every((d) => selectedIds.has(d.id))}
                  onChange={toggleSelectAll}
                  className="w-4 h-4"
                  data-testid="kb-doc-select-all"
                />
                <span>Select all visible</span>
              </label>
              <Filter className="w-4 h-4 text-slate-500 ml-1" />
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="h-9 px-2 rounded-md border border-input bg-background text-sm"
                data-testid="kb-doc-filter-category"
              >
                <option value="">All categories</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                <option value="__uncat">— uncategorized —</option>
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="h-9 px-2 rounded-md border border-input bg-background text-sm"
                data-testid="kb-doc-filter-status"
              >
                <option value="">All statuses</option>
                <option value="pending_review">Pending review</option>
                <option value="ready">Approved</option>
                <option value="processing">Embedding</option>
                <option value="failed">Failed</option>
              </select>
              <span className="text-xs text-slate-500 ml-auto">
                {visibleDocs.length} / {docs.length}
              </span>
            </div>
          )}

          {/* Bulk-action toolbar — appears whenever anything is selected */}
          {selectedIds.size > 0 && (
            <div
              className="sticky top-2 z-20 flex items-center gap-2 p-3 rounded-lg border-2 border-primary/60 bg-primary/5 dark:bg-primary/10 flex-wrap"
              data-testid="kb-doc-bulk-toolbar"
            >
              <span className="text-sm font-semibold" data-testid="kb-doc-bulk-count">
                {selectedIds.size} selected
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={clearSelection}
                data-testid="kb-doc-bulk-clear"
                disabled={reassignBusy}
              >
                Clear
              </Button>
              <div className="mx-1 h-6 w-px bg-slate-300 dark:bg-slate-600" />
              <Button
                size="sm"
                onClick={() => runBulkAction('approve')}
                disabled={reassignBusy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="kb-doc-bulk-approve"
              >
                <CheckCircle2 className="w-4 h-4 mr-1" /> Approve selected
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setBulkAction(bulkAction === 'recategorize' ? null : 'recategorize')}
                disabled={reassignBusy}
                data-testid="kb-doc-bulk-recategorize"
              >
                <Tag className="w-4 h-4 mr-1" /> Re-categorize
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={suggestCategories}
                disabled={suggestBusy || reassignBusy || selectedIds.size > 40}
                data-testid="kb-doc-bulk-suggest"
                title="Ask the AI to propose one category per selected doc based on its preview text"
              >
                {suggestBusy ? (
                  <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Thinking…</>
                ) : (
                  <><Sparkles className="w-4 h-4 mr-1" /> Suggest categories</>
                )}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => runBulkAction('delete')}
                disabled={reassignBusy}
                className="text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                data-testid="kb-doc-bulk-delete"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </Button>
              {bulkAction === 'recategorize' && (
                <div className="w-full flex items-center gap-2 mt-2 pt-2 border-t border-primary/30 flex-wrap">
                  <select
                    value={reassignCategory}
                    onChange={(e) => setReassignCategory(e.target.value)}
                    className="h-9 px-2 rounded-md border border-input bg-background text-sm"
                    data-testid="kb-doc-bulk-reassign-category"
                  >
                    <option value="">— no category change —</option>
                    <option value="__clear">Clear category</option>
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <Input
                    value={reassignTags}
                    onChange={(e) => setReassignTags(e.target.value)}
                    placeholder="Replace tags (comma-sep, blank = don't touch)"
                    className="max-w-sm"
                    data-testid="kb-doc-bulk-reassign-tags"
                  />
                  <Button
                    size="sm"
                    onClick={() => {
                      const body = {};
                      if (reassignCategory) {
                        body.category = reassignCategory === '__clear' ? '' : reassignCategory;
                      }
                      if (reassignTags.trim()) {
                        body.tags = reassignTags.split(',').map((t) => t.trim()).filter(Boolean);
                      }
                      if (!('category' in body) && !('tags' in body)) {
                        toast.error('Pick a category or type tags to apply.');
                        return;
                      }
                      runBulkAction('recategorize', body);
                    }}
                    disabled={reassignBusy}
                    data-testid="kb-doc-bulk-reassign-apply"
                  >
                    Apply to {selectedIds.size}
                  </Button>
                </div>
              )}
              {bulkAction === 'suggest' && suggestions && (
                <div className="w-full mt-2 pt-2 border-t border-primary/30" data-testid="kb-doc-bulk-suggestions">
                  <div className="text-xs font-semibold mb-2 flex items-center justify-between">
                    <span>AI suggestions ({suggestions.length}) — review then apply</span>
                    <button
                      type="button"
                      className="text-slate-500 hover:text-foreground text-xs underline"
                      onClick={() => { setSuggestions(null); setBulkAction(null); }}
                    >
                      Discard
                    </button>
                  </div>
                  <div className="max-h-64 overflow-y-auto space-y-1 rounded-md border border-border bg-background/60 p-2">
                    {suggestions.map((s, i) => {
                      const conf = Math.round((s.confidence || 0) * 100);
                      const chosen = s.override_category || s.suggested_category;
                      return (
                        <div
                          key={s.doc_id}
                          className="flex items-center gap-2 text-xs"
                          data-testid={`kb-doc-suggestion-${s.doc_id}`}
                        >
                          <span className="flex-1 truncate">{s.title}</span>
                          <span
                            className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-mono ${
                              conf >= 75 ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                                : conf >= 40 ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                                : 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200'
                            }`}
                            title={`AI confidence ${conf}%`}
                          >
                            {conf}%
                          </span>
                          <select
                            value={chosen}
                            onChange={(e) => {
                              const v = e.target.value;
                              setSuggestions((prev) => prev.map((row, idx) =>
                                idx === i ? { ...row, override_category: v } : row,
                              ));
                            }}
                            className="h-7 px-1 rounded border border-input bg-background text-xs"
                            data-testid={`kb-doc-suggestion-select-${s.doc_id}`}
                          >
                            <option value="Uncategorized">Uncategorized</option>
                            {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex gap-2 mt-2">
                    <Button
                      size="sm"
                      onClick={applySuggestions}
                      disabled={reassignBusy}
                      data-testid="kb-doc-bulk-suggestions-apply"
                    >
                      Apply {suggestions.length} suggestion{suggestions.length === 1 ? '' : 's'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={suggestCategories}
                      disabled={suggestBusy || reassignBusy}
                      title="Re-run the AI"
                    >
                      <RefreshCw className={`w-4 h-4 mr-1 ${suggestBusy ? 'animate-spin' : ''}`} /> Re-run
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {loading ? (
            <p className="text-sm text-slate-500">Loading documents…</p>
          ) : docs.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">
              No documents ingested yet. Upload SOPs, recipe guides, machine manuals — anything
              the AI should quote when answering members.
            </p>
          ) : (
            <div className="space-y-2">
              {visibleDocs.map((d) => (
                <div
                  key={d.id}
                  className={`p-3 rounded-lg border ${
                    selectedIds.has(d.id)
                      ? 'border-primary/70 bg-primary/5 dark:bg-primary/10'
                      : d.status === 'pending_review'
                      ? 'border-amber-300 bg-amber-50/40 dark:bg-amber-900/10 dark:border-amber-900/40'
                      : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800'
                  }`}
                  data-testid={`kb-doc-row-${d.id}`}
                >
                  <div className="flex justify-between items-start gap-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(d.id)}
                      onChange={() => toggleSelect(d.id)}
                      className="w-4 h-4 mt-1 shrink-0"
                      aria-label={`Select ${d.title}`}
                      data-testid={`kb-doc-select-${d.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium truncate">{d.title}</p>
                        {d.status === 'pending_review' && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-amber-200 text-amber-900 dark:bg-amber-900/60 dark:text-amber-100">
                            <Clock className="w-3 h-3" /> Pending review
                          </span>
                        )}
                        {d.status === 'processing' && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200">
                            <RefreshCw className="w-3 h-3 animate-spin" /> Embedding…
                          </span>
                        )}
                        {d.status === 'ready' && (
                          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
                            <ShieldCheck className="w-3 h-3" /> Approved
                          </span>
                        )}
                        {d.status === 'failed' && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200"
                            title={d.status_message || 'Ingestion failed'}
                          >
                            <AlertTriangle className="w-3 h-3" /> Failed
                          </span>
                        )}
                        {d.duplicate_of && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200"
                            title={`Near-duplicate of doc ${d.duplicate_of}`}
                            data-testid={`kb-doc-dup-${d.id}`}
                          >
                            <Copy className="w-3 h-3" /> Near-duplicate
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 truncate">
                        {d.filename} · {humanSize(d.size)} · {d.chunk_count} chunk{d.chunk_count === 1 ? '' : 's'} · {humanDate(d.created_at)}
                      </p>
                      <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                        {/* Category selector — inline change */}
                        <select
                          value={d.category || ''}
                          onChange={(e) => updateDocMeta(d.id, { category: e.target.value || null })}
                          className="h-7 px-2 text-[11px] rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"
                          data-testid={`kb-doc-category-${d.id}`}
                          title="Change this document's category"
                        >
                          <option value="">— no category —</option>
                          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                        {(d.tags || []).map((t) => (
                          <span
                            key={t}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200"
                          >
                            #{t}
                          </span>
                        ))}
                      </div>
                      {d.preview_text && d.status === 'pending_review' && (
                        <details className="mt-2">
                          <summary className="text-[11px] text-slate-500 cursor-pointer hover:text-slate-700 dark:hover:text-slate-300">
                            Preview extracted text (first 500 chars)
                          </summary>
                          <pre className="text-[11px] whitespace-pre-wrap mt-1 p-2 rounded bg-slate-100 dark:bg-slate-900 text-slate-700 dark:text-slate-300 max-h-40 overflow-y-auto font-mono">
                            {d.preview_text}
                          </pre>
                        </details>
                      )}
                      {d.status === 'failed' && d.status_message ? (
                        <p className="text-[11px] text-rose-600 dark:text-rose-300 mt-1 truncate">
                          {d.status_message}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {d.status === 'pending_review' && (
                        <Button
                          size="sm"
                          onClick={() => approveDoc(d.id, d.title)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          data-testid={`kb-doc-approve-${d.id}`}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                        </Button>
                      )}
                      {d.storage_path ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadDoc(d)}
                          data-testid={`kb-doc-download-${d.id}`}
                          title="Download the original source file"
                        >
                          <FileText className="w-4 h-4 mr-1" /> Download
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setAttachingDocId(d.id);
                            // Delay so state applies before the picker
                            // fires the change event.
                            setTimeout(() => attachRef.current?.click(), 30);
                          }}
                          data-testid={`kb-doc-attach-${d.id}`}
                          title="Legacy doc — upload the original PDF/DOCX so it becomes downloadable"
                        >
                          <Upload className="w-4 h-4 mr-1" /> Attach source
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteDoc(d.id, d.title)}
                        data-testid={`kb-doc-delete-${d.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Coverage report — inline panel; only rendered when opened. */}
      {coverageOpen && (
        <Card data-testid="kb-coverage-panel">
          <CardHeader>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" /> KB coverage report
                </CardTitle>
                <CardDescription>
                  For every open flag, we re-ran retrieval. Anything under {`0.35 cosine`} counts as
                  &ldquo;no coverage&rdquo; and should be your top priority for the next content write.
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={runCoverageReport} disabled={coverageLoading} data-testid="kb-coverage-refresh">
                  <RefreshCw className={`w-4 h-4 mr-2 ${coverageLoading ? 'animate-spin' : ''}`} />
                  Re-run
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setCoverageOpen(false); setCoverage(null); }}
                  aria-label="Close coverage report"
                  data-testid="kb-coverage-close"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {coverageLoading && !coverage ? (
              <p className="text-sm text-slate-500 py-6 text-center">Analyzing coverage across the knowledge base…</p>
            ) : !coverage ? (
              <p className="text-sm text-slate-500 py-6 text-center">Click Re-run to load the report.</p>
            ) : coverage.total_open === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">
                No open flags to analyze — every recent question was answered from your KB.
              </p>
            ) : (
              <>
                {/* Bucket summary */}
                <div className="grid grid-cols-3 gap-2" data-testid="kb-coverage-buckets">
                  <div className="rounded-lg border border-rose-300 bg-rose-50/60 dark:bg-rose-900/10 p-3">
                    <div className="text-xs uppercase tracking-wide font-semibold text-rose-700 dark:text-rose-300">No coverage</div>
                    <div className="text-3xl font-bold mt-1 text-rose-800 dark:text-rose-200">{coverage.buckets.no_coverage}</div>
                    <div className="text-[11px] text-slate-500 mt-1">score &lt; 0.35</div>
                  </div>
                  <div className="rounded-lg border border-amber-300 bg-amber-50/60 dark:bg-amber-900/10 p-3">
                    <div className="text-xs uppercase tracking-wide font-semibold text-amber-700 dark:text-amber-300">Weak coverage</div>
                    <div className="text-3xl font-bold mt-1 text-amber-800 dark:text-amber-200">{coverage.buckets.weak_coverage}</div>
                    <div className="text-[11px] text-slate-500 mt-1">0.35–0.55</div>
                  </div>
                  <div className="rounded-lg border border-emerald-300 bg-emerald-50/60 dark:bg-emerald-900/10 p-3">
                    <div className="text-xs uppercase tracking-wide font-semibold text-emerald-700 dark:text-emerald-300">Has coverage</div>
                    <div className="text-3xl font-bold mt-1 text-emerald-800 dark:text-emerald-200">{coverage.buckets.has_coverage}</div>
                    <div className="text-[11px] text-slate-500 mt-1">≥ 0.55</div>
                  </div>
                </div>

                {/* No coverage list — highest priority */}
                {coverage.no_coverage.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm text-slate-900 dark:text-white mb-2">
                      🔴 Write these first
                    </h4>
                    <div className="space-y-2" data-testid="kb-coverage-no-list">
                      {coverage.no_coverage.slice(0, 15).map((r) => (
                        <div key={r.flag_id} className="rounded-lg border border-rose-200 dark:border-rose-900/50 p-3 bg-rose-50/40 dark:bg-rose-900/5">
                          <div className="flex justify-between items-start gap-3">
                            <p className="text-sm font-medium">&ldquo;{r.question}&rdquo;</p>
                            <span className="text-[10px] uppercase tracking-wide bg-rose-200 dark:bg-rose-900/60 text-rose-800 dark:text-rose-200 px-2 py-0.5 rounded whitespace-nowrap">
                              score {r.best_score.toFixed(2)}
                            </span>
                          </div>
                          {r.best_snippet ? (
                            <p className="text-[11px] text-slate-500 mt-1">
                              Closest match ({r.best_source_title || 'unknown'}): &ldquo;{r.best_snippet}&hellip;&rdquo;
                            </p>
                          ) : (
                            <p className="text-[11px] text-slate-500 mt-1 italic">No matching chunk in the KB at all.</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Weak coverage — smaller fixes */}
                {coverage.weak_coverage.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm text-slate-900 dark:text-white mb-2">
                      🟡 Improve these next
                    </h4>
                    <ul className="text-sm space-y-1" data-testid="kb-coverage-weak-list">
                      {coverage.weak_coverage.slice(0, 10).map((r) => (
                        <li key={r.flag_id} className="flex justify-between items-start gap-3">
                          <span>&ldquo;{r.question}&rdquo;</span>
                          <span className="text-xs text-amber-700 dark:text-amber-300 whitespace-nowrap">score {r.best_score.toFixed(2)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <p className="text-[11px] text-slate-400 mt-2">
                  Generated {coverage.generated_at ? new Date(coverage.generated_at).toLocaleString() : ''} · {coverage.total_open} open flag{coverage.total_open === 1 ? '' : 's'} analyzed.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Unanswered Questions */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Unanswered Questions
                {flags.length > 0 && (
                  <span className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 px-2 py-0.5 rounded-full">
                    {flags.length} open
                  </span>
                )}
              </CardTitle>
              <CardDescription>
                  When the AI can&apos;t confidently answer a member&apos;s question from your knowledge base,
                  it says &quot;I don&apos;t know&quot; and files a flag here so you can add coverage.
              </CardDescription>
            </div>
            {flags.length > 0 && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    if (!window.confirm(`Mark all ${flags.length} open questions as resolved? They stay in the audit trail so coverage reports still track history.`)) return;
                    try {
                      const { data } = await axios.post(`${API}/admin/ai-knowledge/flags/clear-all?mode=resolve`);
                      toast.success(`Cleared ${data.cleared} open question${data.cleared === 1 ? '' : 's'}.`);
                      await loadAll();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Clear failed');
                    }
                  }}
                  data-testid="kb-flags-clear-resolve-btn"
                  title="Mark every open flag as resolved (keeps history)"
                >
                  Clear all (resolve)
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    if (!window.confirm(`Permanently DELETE all ${flags.length} open questions? This cannot be undone and removes them from the audit trail.`)) return;
                    try {
                      const { data } = await axios.post(`${API}/admin/ai-knowledge/flags/clear-all?mode=delete`);
                      toast.success(`Deleted ${data.cleared} question${data.cleared === 1 ? '' : 's'}.`);
                      await loadAll();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Delete failed');
                    }
                  }}
                  data-testid="kb-flags-clear-delete-btn"
                  className="text-rose-700 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                  title="Hard-delete every open flag (no history kept)"
                >
                  Delete all
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-slate-500">Loading flags…</p>
          ) : flags.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center">
              No open flags. Every question in the last cycle was answered from the knowledge base.
            </p>
          ) : (
            <div className="space-y-3">
              {flags.map((f) => (
                <div
                  key={f.id}
                  className="p-4 rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50/50 dark:bg-amber-900/10 space-y-2"
                  data-testid={`kb-flag-row-${f.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm font-medium text-slate-900 dark:text-white flex-1">
                      &ldquo;{f.question}&rdquo;
                    </p>
                    <span
                      className="text-[10px] uppercase tracking-wide px-2 py-1 rounded bg-slate-200 dark:bg-slate-700 whitespace-nowrap"
                      title="Cosine similarity of the best chunk retrieved. Below 0.35 = we treat it as I-don-t-know."
                    >
                      best {(f.best_score ?? 0).toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500">
                    Asked by {f.asked_by_email || 'unknown'} · {humanDate(f.created_at)}
                    {f.context ? ` · context: ${f.context}` : ''}
                  </p>
                  <Textarea
                    rows={2}
                    placeholder="Optional: note what content you added to address this."
                    value={resolutionDrafts[f.id] || ''}
                    onChange={(e) => setResolutionDrafts((s) => ({ ...s, [f.id]: e.target.value }))}
                    data-testid={`kb-flag-note-${f.id}`}
                  />
                  <div className="flex gap-2 justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => dismissFlag(f.id)}
                      data-testid={`kb-flag-dismiss-${f.id}`}
                    >
                      Dismiss
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => resolveFlag(f.id)}
                      data-testid={`kb-flag-resolve-${f.id}`}
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Mark addressed
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
